import React from 'react';
import {
  Box,
  IconButton,
  Slider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Radar as RadarIcon,
  TuneRounded as TuneIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPhaseConfig } from '../PhaseChip';

const PHASES = ['accumulation', 'markup', 'distribution', 'absorption'];

export default function StockPhasesToolbar({
  activePhase,
  onPhaseChange,
  summary,
  showFilters,
  onToggleFilters,
  showScores,
  onToggleScores,
  visibleCount,
  totalCount,
  minConfidence,
  onMinConfidenceChange,
  onMinConfidenceCommit,
}) {
  const { t } = useTranslation();
  const activePhaseConfig = getPhaseConfig(activePhase);

  return (
    <>
      <Box sx={{
        mt: 3,
        mb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: { xs: 1.5, sm: 1 },
      }}>
        <Tabs
          value={activePhase}
          onChange={(_, value) => onPhaseChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            maxWidth: '100%',
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: activePhaseConfig.color,
            },
            '& .MuiTab-root': {
              fontWeight: 700,
              textTransform: 'none',
              minHeight: 40,
              fontSize: '0.82rem',
              color: '#94a3b8',
              px: 2,
              '&.Mui-selected': { color: activePhaseConfig.color },
            },
          }}
        >
          {PHASES.map((phase) => {
            const config = getPhaseConfig(phase);
            const count = summary?.counts?.[phase]?.count || 0;
            const isActive = activePhase === phase;

            return (
              <Tab
                key={phase}
                value={phase}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{t(`phase.phases.${phase}`)}</span>
                    <Box sx={{
                      px: 0.8,
                      py: 0.15,
                      borderRadius: '6px',
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      fontFamily: 'monospace',
                      backgroundColor: isActive ? `${config.color}15` : '#f1f5f9',
                      color: isActive ? config.color : '#94a3b8',
                      border: `1px solid ${isActive ? `${config.color}30` : '#e2e8f0'}`,
                      lineHeight: '16px',
                      minWidth: 22,
                      textAlign: 'center',
                    }}>
                      {count}
                    </Box>
                  </Box>
                )}
              />
            );
          })}
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={t('phase.filterAdvanced')} arrow>
            <IconButton
              size="small"
              onClick={onToggleFilters}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                width: 36,
                height: 36,
                backgroundColor: showFilters ? '#667eea10' : '#fff',
                color: showFilters ? '#667eea' : '#94a3b8',
                '&:hover': { borderColor: '#667eea', color: '#667eea' },
              }}
            >
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={showScores ? t('phase.hideScores') : t('phase.showScores')} arrow>
            <IconButton
              size="small"
              onClick={onToggleScores}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                width: 36,
                height: 36,
                backgroundColor: showScores ? '#667eea10' : '#fff',
                color: showScores ? '#667eea' : '#94a3b8',
                '&:hover': { borderColor: '#667eea', color: '#667eea' },
              }}
            >
              <RadarIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Box sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '8px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
          }}>
            <Typography sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#64748b',
            }}>
              {visibleCount} / {totalCount}
            </Typography>
          </Box>
        </Box>
      </Box>

      {showFilters && (
        <Box sx={{
          mb: 2,
          p: 2,
          background: '#fff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 3 },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('phase.minConfidence')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, maxWidth: 300, width: { xs: '100%', sm: 'auto' } }}>
            <Slider
              value={minConfidence}
              onChange={(_, value) => onMinConfidenceChange(value)}
              onChangeCommitted={onMinConfidenceCommit}
              min={0}
              max={0.8}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
              sx={{
                color: '#667eea',
                '& .MuiSlider-thumb': {
                  width: 16,
                  height: 16,
                  '&:hover, &.Mui-active': {
                    boxShadow: '0 0 0 6px rgba(102,126,234,0.15)',
                  },
                },
                '& .MuiSlider-track': {
                  height: 4,
                  borderRadius: 2,
                },
                '& .MuiSlider-rail': {
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: '#e2e8f0',
                },
              }}
            />
            <Typography sx={{
              fontSize: '0.82rem',
              fontWeight: 700,
              fontFamily: 'monospace',
              color: '#667eea',
              minWidth: 40,
            }}>
              {(minConfidence * 100).toFixed(0)}%
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}